/**
 * 政府端 — 监管驾驶舱（在途车辆总览）
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchDashboard } from '@/api/transit';
import { Empty, Loading, PageHeader, StatCard } from '@/components/common';
import { OrderStatusTag } from '@/components/common';
import type { DashboardData } from '@/types';
import { alertLevelTone, fmtTime } from '@/utils/format';

export default function GovDashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard().then((d) => {
      setData(d);
      setLoading(false);
    });
  }, []);

  if (loading) return <Loading />;
  if (!data) return <Empty />;

  const stat = data.alert_statistics ?? {};

  return (
    <div>
      <PageHeader title="监管驾驶舱" sub="在途车辆、告警与运输单实时总览" />

      <div className="grid grid-4" style={{ marginBottom: 16 }}>
        <StatCard label="在途车辆" value={data.in_transit_count} tone="blue" icon="🚚" />
        <StatCard label="今日告警" value={stat.today_total ?? 0} tone="red" icon="⚠️" />
        <StatCard label="待处理告警" value={stat.pending_total ?? 0} tone="amber" icon="🕐" />
        <StatCard label="证照待核验" value={data.recent_orders.filter((o) => o.quarantine_status === 3).length} tone="green" icon="📋" />
      </div>

      <div className="grid grid-2">
        <div className="card">
          <div className="card-title">
            <span>🚚 在途车辆位置</span>
            <Link to="/gov/vehicles" style={{ fontSize: 13 }}>查看全部 →</Link>
          </div>
          {data.vehicle_list.length === 0 ? (
            <Empty text="暂无在途车辆" />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>车牌</th>
                  <th>运输单号</th>
                  <th>位置</th>
                  <th>速度</th>
                  <th>告警</th>
                </tr>
              </thead>
              <tbody>
                {data.vehicle_list.map((v) => (
                  <tr key={v.transit_id}>
                    <td>
                      <Link to={`/gov/vehicles/${v.transit_id}`} style={{ fontWeight: 700 }}>
                        {v.plate_no}
                      </Link>
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{v.transit_no}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {v.lng.toFixed(4)}, {v.lat.toFixed(4)}
                    </td>
                    <td>{v.speed.toFixed(1)} km/h</td>
                    <td>
                      {v.alert_level > 0 ? (
                        <span style={{ color: 'var(--color-red)', fontWeight: 700 }}>⚠ {alertLevelTone(v.alert_level)}</span>
                      ) : (
                        <span style={{ color: 'var(--color-green)' }}>正常</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="card">
          <div className="card-title">
            <span>📦 最近运输单</span>
            <Link to="/enterprise/orders" style={{ fontSize: 13 }}>企业看板 →</Link>
          </div>
          {data.recent_orders.length === 0 ? (
            <Empty />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>运输单号</th>
                  <th>路线</th>
                  <th>状态</th>
                  <th>检疫</th>
                  <th>最近上报</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_orders.map((o) => (
                  <tr key={o.id}>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{o.transit_no}</td>
                    <td>
                      {o.origin_city} → {o.destination_city}
                    </td>
                    <td>
                      <OrderStatusTag status={o.status} />
                    </td>
                    <td>{o.quarantine_status === 1 ? '✅ 通过' : o.quarantine_status === 3 ? '⏳ 待核验' : '—'}</td>
                    <td style={{ fontSize: 12, color: 'var(--color-ink-secondary)' }}>{fmtTime(o.last_gps_time)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
